/**
 * 算法处理器注册表
 *
 * 新增算法时：
 * 1. 在本文件注册新的 algorithmType → apiMethod 映射
 * 2. 在 contant.js 添加图片列表和配置
 * 3. 完成（无需改 recognition-item.vue）
 */

import {
  runFaceTask,
  bodyRecognition,
  commonOcr,
  petCompare,
} from '@/api/upload.js'
import { imageTechnology } from '@/api/image.js'

/**
 * 算法处理器映射表
 * key: algorithmType（从 API 返回的类型标识）
 * value: {
 *   apiMethod: 调用的 API 函数,
 *   supportsCrop: 是否支持裁剪显示,
 *   category: 用于确定图片来源的分类
 * }
 */
export const algorithmProcessors = {
  // 人脸类
  face_detection: {
    apiMethod: runFaceTask,
    supportsCrop: true,
    category: 'face',
  },
  face_landmarks_detection: {
    apiMethod: runFaceTask,
    supportsCrop: true,
    category: 'face',
  },
  face_age: { apiMethod: runFaceTask, supportsCrop: true, category: 'face' },
  face_gender: { apiMethod: runFaceTask, supportsCrop: true, category: 'face' },
  face_emotion: {
    apiMethod: runFaceTask,
    supportsCrop: true,
    category: 'face',
  },
  face_mask_detection: {
    apiMethod: runFaceTask,
    supportsCrop: true,
    category: 'face',
  },

  // 人体类
  body_detection: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_keypoint: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_attribute: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_human_segmentation: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_crowd_density: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_hand_keypoint: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_driver_monitoring: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_helmet_detection: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_head_shoulder_detection: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_gesture_recognition: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_reflective_vest_detection: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },
  body_detection_smoking: {
    apiMethod: bodyRecognition,
    supportsCrop: true,
    category: 'body',
  },

  // OCR 类
  qr_code: { apiMethod: commonOcr, supportsCrop: true, category: 'ocr' },
  ocr_vehicle_plate: {
    apiMethod: commonOcr,
    supportsCrop: true,
    category: 'ocr',
  },
  ocr_business_license: {
    apiMethod: commonOcr,
    supportsCrop: true,
    category: 'ocr',
  },
  ocr_bank_card: { apiMethod: commonOcr, supportsCrop: true, category: 'ocr' },
  ocr_invoice_information: {
    apiMethod: commonOcr,
    supportsCrop: true,
    category: 'ocr',
  },
  ocr_driver_license: {
    apiMethod: commonOcr,
    supportsCrop: true,
    category: 'ocr',
  },

  // 分类/检测类
  vehicle_detection: {
    apiMethod: petCompare,
    supportsCrop: true,
    category: 'vehicle',
  },
  detection_fire: {
    apiMethod: petCompare,
    supportsCrop: true,
    category: 'detection',
  },
  classification_general: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: 'classification',
  },
  classification_vegetables: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: 'classification',
  },
  classification_animal: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: 'classification',
  },
  classification_vehicle_type: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: 'classification',
  },
  classification_dishes: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: 'classification',
  },
  ocr_water_meter: {
    apiMethod: petCompare,
    supportsCrop: false,
    category: "classification",
  },

  // 图像处理类
  image_tech_super_resolution: {
    apiMethod: imageTechnology,
    supportsCrop: false,
    category: 'imagetech',
  },
  image_tech_coloring: {
    apiMethod: imageTechnology,
    supportsCrop: false,
    category: 'imagetech',
  },
  classification_landmark: {
    apiMethod: imageTechnology,
    supportsCrop: false,
    category: 'imagetech',
  },
}

/**
 * 获取算法处理器
 * @param {string} algorithmType - 算法类型标识
 * @returns {object} 处理器配置
 */
export function getAlgorithmProcessor(algorithmType) {
  const processor = algorithmProcessors[algorithmType]
  if (!processor) {
    console.warn(`Algorithm processor not found: ${algorithmType}`)
    return null
  }
  return processor
}

/**
 * 检查是否为 OCR 类算法
 */
export const ocrTypeSet = new Set(
  Object.entries(algorithmProcessors)
    .filter(([_, config]) => config.category === 'ocr')
    .map(([type]) => type),
)

/**
 * 获取算法图片列表映射（从 contant.js 导入的列表）
 * 新增算法时需要在 contant.js 添加对应的列表
 */
export function getAlgorithmImageList(algorithmType, allLists) {
  // allLists 是从 contant.js 传入的所有列表对象
  return allLists[algorithmType] || []
}
